import { useState } from "react";
import SectionTitle from "@/components/SectionTitle";
import Button from "@/components/Button";
import { SOCIAL, CONTACT } from "@/config";

export default function Contact() {

  function GitHubIcon(props) {
    return (
      <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden {...props}>
        <path d="M12 .5A11.5 11.5 0 0 0 .5 12.3c0 5.2 3.3 9.6 7.9 11.1.6.1.8-.3.8-.6v-2.2c-3.2.7-3.9-1.4-3.9-1.4-.5-1.2-1.2-1.5-1.2-1.5-1-.7.1-.7.1-.7 1.1.1 1.7 1.1 1.7 1.1 1 .1.9 1.9 2.9 1.4.1-.8.4-1.3.7-1.6-2.6-.3-5.3-1.4-5.3-6.1 0-1.3.5-2.4 1.2-3.3-.1-.3-.6-1.6.1-3.4 0 0 1-.3 3.4 1.2a11.6 11.6 0 0 1 6.1 0c2.4-1.5 3.4-1.2 3.4-1.2.7 1.8.2 3.1.1 3.4.8.9 1.2 2 1.2 3.3 0 4.7-2.7 5.8-5.3 6.1.4.3.8 1 .8 2.1v3.1c0 .3.2.7.8.6 4.7-1.5 7.9-5.9 7.9-11.1A11.5 11.5 0 0 0 12 .5z" />
      </svg>
    );
  }

  function LinkedInIcon(props) {
    return (
      <svg viewBox="0 0 16 16" fill="currentColor" aria-hidden {...props}>
        <path d="M0 1.146C0 .513.526 0 1.175 0h13.65C15.474 0 16 .513 16 1.146v13.708c0 .633-.526 1.146-1.175 1.146H1.175C.526 16 0 15.487 0 14.854zM4.943 13.394V6.169H2.542v7.225zm-.943-8.473c.837 0 1.358-.554 1.358-1.248-.015-.709-.52-1.248-1.342-1.248S2.4 3.226 2.4 3.934c0 .694.521 1.248 1.327 1.248zM13.458 13.394V9.359c0-2.22-1.184-3.252-2.764-3.252-1.274 0-1.845.7-2.165 1.193V6.169H6.129c.03.678 0 7.225 0 7.225h2.4V9.7c0-.216.016-.432.08-.586.173-.431.568-.878 1.232-.878.869 0 1.216.662 1.216 1.634v3.865h2.401z" />
      </svg>
    );
  }

  function TwitterIcon(props) {
    return (
      <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden {...props}>
        <path d="M19.6 7.4c.5-1 .8-2 .9-3-1 .6-2 .9-3.1 1.1A3.7 3.7 0 0 0 12 8.8c0 .3 0 .6.1.8C8.3 9.5 5.1 7.9 3 5.3c-.4.8-.6 1.6-.6 2.5 0 1.7.9 3.2 2.2 4.1-.8 0-1.5-.2-2.1-.6v.1c0 2.4 1.7 4.4 3.9 4.9-.4.1-.9.2-1.4.2-.3 0-.6 0-.8-.1.6 2 2.5 3.4 4.7 3.5A7.4 7.4 0 0 1 2 19.6a10.4 10.4 0 0 0 5.6 1.7c6.7 0 10.4-5.6 10.4-10.4v-.5c.7-.5 1.4-1.2 1.9-2z" />
      </svg>
    );
  }

  function InstagramIcon(props) {
    return (
      <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden {...props}>
        <path d="M7 2h10a5 5 0 0 1 5 5v10a5 5 0 0 1-5 5H7a5 5 0 0 1-5-5V7a5 5 0 0 1 5-5Zm5 5a5 5 0 1 0 0 10 5 5 0 0 0 0-10Zm6.5-.9a1.1 1.1 0 1 0 0 2.2 1.1 1.1 0 0 0 0-2.2Z" />
      </svg>
    );
  }

  function MailIcon(props) {
    return (
      <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden {...props}>
        <path d="M4 5h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2Zm8 6L4.5 7.8V7h15v.8L12 11Z" />
      </svg>
    );
  }

  const [status, setStatus] = useState("idle");

  const onSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      const form = e.currentTarget;
      const data = Object.fromEntries(new FormData(form).entries());
      if (CONTACT.formEndpoint) {
        const res = await fetch(CONTACT.formEndpoint, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data),
        });
        if (!res.ok) throw new Error("Request failed");
      } else {
        await new Promise((r) => setTimeout(r, 800));
      }
      setStatus("success");
      form.reset();
    } catch (e) {
      setStatus("error");
    } finally {
      setTimeout(() => setStatus("idle"), 3000);
    }
  };

  return (
    <section className="relative py-20 sm:py-28">
      {/* Inline CSS for this section (button + inputs theme) */}
      <style>{`
        /* Inputs */
        .contact-input, .contact-textarea {
          border-radius: 0.75rem;
          border: 1px solid rgba(255,255,255,0.10);
          background: color-mix(in oklab, var(--color-bg-dark) 80%, transparent);
          color: var(--color-fg-dark);
        }
        .contact-input::placeholder, .contact-textarea::placeholder {
          color: rgb(156 163 175 / 0.8);
        }
        .contact-input:focus, .contact-textarea:focus {
          outline: none;
          border-color: rgba(255,255,255,0.20);
        }

        /* Contact submit button uses neon glow pill like "Let’s chat" */
        .btn-contact {
          position: relative;
          border-radius: 9999px;
          color: #0b0b0b;
          background: #ffffff;
          transition: box-shadow .2s ease, transform .08s ease;
        }
        .dark .btn-contact { color: #0b0b0b; background: #fafafa; }
        .btn-contact::before {
          content: "";
          position: absolute;
          inset: -2px;
          z-index: -1;
          border-radius: inherit;
          background: linear-gradient(90deg, rgba(0,245,160,.9), rgba(255,0,127,.9), rgba(140,48,255,.9));
          filter: blur(12px);
        }
        .btn-contact {
          box-shadow:
            inset 0 1px 0 rgba(255,255,255,.95),
            0 1px 2px rgba(0,0,0,.06);
        }
        .btn-contact:hover {
          box-shadow:
            inset 0 1px 0 rgba(255,255,255,1),
            0 2px 8px rgba(0,0,0,.12);
        }
        .btn-contact:active { transform: translateY(1px); }

        /* Elsewhere card */
        .elsewhere-card {
          border: 1px solid rgba(255,255,255,0.10);
          background: color-mix(in oklab, var(--color-bg-dark) 70%, transparent);
          backdrop-filter: blur(8px);
          border-radius: 1rem;
        }
        .elsewhere-link { transition: color .15s ease; }
        .elsewhere-link:hover { color: var(--color-primary); }
      `}</style>

      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <SectionTitle
          eyebrow="Contact"
          title="Ready to build something together?"
          subtitle="Drop a message and let’s discuss your idea, collaboration, or just say hi."
          center
        />

        <div className="mt-10 grid grid-cols-1 md:grid-cols-5 gap-8">
          <div className="md:col-span-3">
            <form onSubmit={onSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input name="name" label="Name" placeholder="Your name" required />
                <Input name="email" type="email" label="Email" placeholder="you@example.com" required />
              </div>
              <div>
                <Label htmlFor="message">Message</Label>
                <textarea
                  id="message"
                  name="message"
                  placeholder="Tell me about your project..."
                  className="contact-textarea mt-2 h-36 w-full resize-y p-3"
                  required
                />
              </div>

              <div className="flex items-center gap-3">
                <Button
                  type="submit"
                  as="button"
                  variant="glow"
                  size="lg"
                  className="btn-contact text-black dark:text-black"
                  disabled={status === "loading"}
                >
                  {status === "loading" ? "Sending..." : "Send message"}
                </Button>

                {status === "success" && (
                  <p className="text-sm text-green-500">{CONTACT.successMessage}</p>
                )}
                {status === "error" && (
                  <p className="text-sm text-red-500">{CONTACT.errorMessage}</p>
                )}
              </div>
            </form>
          </div>

          <div className="md:col-span-2">
            <div className="elsewhere-card p-6">
              <h4 className="font-medium">Elsewhere</h4>
              <ul className="mt-3 space-y-2 text-sm">
                {SOCIAL.github && (
                  <li>
                    <a className="elsewhere-link inline-flex items-center gap-2" href={SOCIAL.github} target="_blank" rel="noreferrer">
                      <GitHubIcon className="h-4 w-4 text-[--color-primary]" />
                      GitHub
                    </a>
                  </li>
                )}
                {SOCIAL.linkedin && (
                  <li>
                    <a className="elsewhere-link inline-flex items-center gap-2" href={SOCIAL.linkedin} target="_blank" rel="noreferrer">
                      <LinkedInIcon className="h-4 w-4 text-[--color-primary]" />
                      LinkedIn
                    </a>
                  </li>
                )}
                {SOCIAL.twitter && (
                  <li>
                    <a className="elsewhere-link inline-flex items-center gap-2" href={SOCIAL.twitter} target="_blank" rel="noreferrer">
                      <TwitterIcon className="h-4 w-4 text-[--color-primary]" />
                      Twitter
                    </a>
                  </li>
                )}
                {SOCIAL.instagram && (
                  <li>
                    <a className="elsewhere-link inline-flex items-center gap-2" href={SOCIAL.instagram} target="_blank" rel="noreferrer">
                      <InstagramIcon className="h-4 w-4 text-[--color-primary]" />
                      Instagram
                    </a>
                  </li>
                )}
                {SOCIAL.email && (
                  <li className="pt-2 text-gray-400 inline-flex items-center gap-2">
                    <MailIcon className="h-4 w-4 text-[--color-primary]" />
                    {SOCIAL.email}
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Ambient blobs */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute right-[10%] top-[18%] h-24 w-24 rounded-full bg-neon-mint/20 blur-2xl" />
        <div className="absolute left-[8%] bottom-[16%] h-28 w-28 rounded-full bg-neon-violet/20 blur-2xl" />
      </div>
    </section>
  );
}

function Input({ label, name, type = "text", placeholder, required }) {
  return (
    <div>
      <Label htmlFor={name}>{label}</Label>
      <input
        id={name}
        name={name}
        type={type}
        placeholder={placeholder}
        required={required}
        className="contact-input mt-2 h-11 w-full px-3"
      />
    </div>
  );
}

function Label(props) {
  return (
    <label
      {...props}
      className="block text-xs uppercase tracking-wider text-gray-500"
    />
  );
}